/**
 * Coach admin helpers for Garmin Running Signature display.
 */

export const RULE_LABELS = {
  low_cadence: 'Low cadence',
  cadence_fade: 'Cadence fade',
  pace_fade: 'Pace fade',
  hr_drift: 'Heart rate drift',
  high_vertical_ratio: 'High vertical ratio',
  long_gct: 'Long ground contact',
  gct_fade: 'Ground contact fade',
  overstride_combo: 'Overstride pattern',
  lr_imbalance: 'Left/right imbalance',
};

export function humanizeRuleKey(ruleKey) {
  if (!ruleKey) return null;
  return RULE_LABELS[ruleKey] || ruleKey.replace(/_/g, ' ');
}

export function humanizeEnum(value) {
  if (!value || value === 'unknown') return 'Unknown';
  return String(value)
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatSignatureVerdict(signature) {
  if (!signature) return 'Not analyzed';
  if (signature.status === 'pending') return 'Analysis pending';
  if (signature.status === 'failed') return 'Analysis failed';
  if (signature.status === 'insufficient_data') return 'Insufficient data';
  if (signature.status === 'complete' && signature.primary_rule) {
    return humanizeRuleKey(signature.primary_rule);
  }
  if (signature.status === 'complete' && !signature.primary_rule) {
    return 'Looking good';
  }
  return signature.status || 'Unknown';
}

export function statusBadgeVariant(status, primaryRule) {
  if (status === 'failed') return 'destructive';
  if (status === 'pending') return 'secondary';
  if (status === 'insufficient_data') return 'warning';
  if (status === 'complete' && primaryRule) return 'default';
  if (status === 'complete') return 'success';
  return 'outline';
}

export function checkStatusVariant(checkStatus) {
  switch (checkStatus) {
    case 'triggered':
      return 'default';
    case 'passed':
      return 'success';
    case 'skipped':
      return 'secondary';
    case 'unavailable':
      return 'outline';
    default:
      return 'outline';
  }
}

export function formatCheckValue(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(2);
}

export function formatThreshold(threshold) {
  if (!threshold || typeof threshold !== 'object') return '—';
  return Object.entries(threshold)
    .map(([key, val]) => `${key.replace(/_/g, ' ')}: ${val}`)
    .join(', ');
}

export function formatEvidence(evidence) {
  if (!evidence || typeof evidence !== 'object') return [];
  return Object.entries(evidence).filter(([, val]) => val != null && val !== '');
}

export function formatDurationSeconds(seconds) {
  if (seconds == null) return '—';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;
    return `${hours}h ${remMins}m`;
  }
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

export function formatCoverage(fraction) {
  if (fraction == null) return '—';
  return `${Math.round(Number(fraction) * 100)}%`;
}

export function truncateFingerprint(fingerprint) {
  if (!fingerprint) return '—';
  if (fingerprint.length <= 12) return fingerprint;
  return `${fingerprint.slice(0, 8)}…${fingerprint.slice(-4)}`;
}
